import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, Clock, Copy, Share2, ShieldCheck, Ticket, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { api, type IssuedGuestLink, type Meeting, type MeetingGuestLink } from '../../lib/api'
import { notify } from '../../lib/notify'
import { cn } from '../../lib/utils'
import { confirm } from '../ui/confirm-dialog'

// #751: one-time guest links for customers. Only admins issue them, and
// only for meetings in our own Jitsi room (roomSlug set) that are not past
// end + 2 h. The plaintext link exists only right after issuing (the server
// keeps a hash); after a reload the block says so and offers a new one.

const MEETING_GRACE_MS = 2 * 60 * 60 * 1000

export function canIssueGuestLink(opts: {
  isAdmin: boolean
  meeting: Pick<Meeting, 'roomSlug' | 'endTime'>
  now: Date
}): boolean {
  if (!opts.isAdmin || !opts.meeting.roomSlug) return false
  return opts.now.getTime() <= new Date(opts.meeting.endTime).getTime() + MEETING_GRACE_MS
}

/** Links that can still be redeemed, newest first. */
export function openGuestLinks(links: MeetingGuestLink[], now: Date): MeetingGuestLink[] {
  return links
    .filter((l) => !l.usedAt && !l.revokedAt && new Date(l.expiresAt).getTime() > now.getTime())
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
}

/** Redeemed links whose bound browser can still rejoin (until meeting end
 * + 2 h) — the admin must be able to revoke these too. Newest first. */
export function redeemedGuestLinks(
  links: MeetingGuestLink[],
  meeting: Pick<Meeting, 'endTime'>,
  now: Date,
): MeetingGuestLink[] {
  const cutoff = new Date(meeting.endTime).getTime() + MEETING_GRACE_MS
  return links
    .filter((l) => l.usedAt && !l.revokedAt && now.getTime() <= cutoff)
    .sort((a, b) => new Date(b.usedAt ?? 0).getTime() - new Date(a.usedAt ?? 0).getTime())
}

/** An unused link stops working at min(24 h, meeting end + 2 h). */
export function guestLinkDeadline(
  link: Pick<MeetingGuestLink, 'expiresAt'>,
  meeting: Pick<Meeting, 'endTime'>,
): { at: string; byMeeting: boolean } {
  const expires = new Date(link.expiresAt).getTime()
  const meetingCutoff = new Date(meeting.endTime).getTime() + MEETING_GRACE_MS
  return meetingCutoff < expires
    ? { at: new Date(meetingCutoff).toISOString(), byMeeting: true }
    : { at: link.expiresAt, byMeeting: false }
}

const stampFmt = new Intl.DateTimeFormat('de-DE', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

/** „Do., 24.09., 16:00" */
function stamp(iso: string): string {
  return stampFmt.format(new Date(iso))
}

function displayUrl(url: string): string {
  try {
    const u = new URL(url)
    return `${u.host}${u.pathname}${u.hash}`
  } catch {
    return url
  }
}

const ISSUE_ERRORS: Record<string, string> = {
  too_many_open: 'Schon 5 offene Gastlinks — erst einen widerrufen',
  meeting_over: 'Das Meeting ist vorbei',
  not_managed: 'Gastlinks gibt es nur für Meetings im eigenen Jitsi-Raum',
}

function useGuestLinks(meeting: Meeting, isAdmin: boolean, clock?: Date) {
  const qc = useQueryClient()
  const now = clock ?? new Date()
  const enabled = canIssueGuestLink({ isAdmin, meeting, now })
  const queryKey = ['meetings', meeting.id, 'guest-links']
  const { data } = useQuery({
    queryKey,
    queryFn: () => api.listGuestLinks(meeting.id),
    enabled,
  })
  const open = openGuestLinks(data?.links ?? [], now)
  const redeemed = redeemedGuestLinks(data?.links ?? [], meeting, now)
  return { qc, enabled, queryKey, open, redeemed }
}

/** Action-row part: „Gastlink ausstellen" or the „Gastlink offen" pill. */
export function GuestLinkAction({
  meeting,
  isAdmin,
  expanded,
  onToggle,
  onIssued,
  now,
}: {
  meeting: Meeting
  isAdmin: boolean
  expanded?: boolean
  onToggle?: () => void
  onIssued?: (link: IssuedGuestLink) => void
  /** Clock override (tests); defaults to the current time. */
  now?: Date
}) {
  const { qc, enabled, queryKey, open, redeemed } = useGuestLinks(meeting, isAdmin, now)
  const issue = useIssueGuestLink(meeting.id, queryKey, qc, onIssued)
  if (!enabled) return null
  if (open.length > 0 || redeemed.length > 0) {
    return (
      // Transparentes Tap-Ziel (44 px per index.css), darin die kleine Pill.
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!!expanded}
        className="group inline-flex items-center shrink-0"
        title={expanded ? 'Gastlink ausblenden' : 'Gastlink anzeigen'}
      >
        <span className="inline-flex items-center gap-1 rounded-full bg-success/15 text-success px-2 py-0.5 text-2xs font-medium whitespace-nowrap group-hover:bg-success/25 transition-colors">
          <UserPlus className="h-3 w-3" />
          {open.length > 0 ? 'Gastlink offen' : 'Gastlink eingelöst'}
          {open.length > 1 && <span className="tabular-nums">· {open.length}</span>}
        </span>
      </button>
    )
  }
  return (
    <button
      type="button"
      onClick={() => issue.mutate()}
      disabled={issue.isPending}
      className="inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap rounded-md border border-accent/30 px-2 py-1 text-xs font-medium text-accent hover:bg-accent/10 transition-colors disabled:opacity-50"
    >
      <UserPlus className="h-3.5 w-3.5" />
      {issue.isPending ? '…' : 'Gastlink ausstellen'}
    </button>
  )
}

function useIssueGuestLink(
  meetingId: string,
  queryKey: string[],
  qc: ReturnType<typeof useQueryClient>,
  onIssued?: (link: IssuedGuestLink) => void,
) {
  return useMutation({
    mutationFn: () => api.issueGuestLink(meetingId),
    onSuccess: ({ link }) => {
      // Sofort in die Liste übernehmen, damit Pill + Block ohne Refetch-Lücke erscheinen.
      qc.setQueryData<{ links: MeetingGuestLink[] }>(queryKey, (old) => ({
        links: [
          {
            id: link.id,
            createdAt: link.createdAt,
            expiresAt: link.expiresAt,
            usedAt: null,
            revokedAt: null,
          },
          ...(old?.links ?? []),
        ],
      }))
      onIssued?.(link)
      qc.invalidateQueries({ queryKey })
    },
    onError: (err: Error & { code?: string }) =>
      notify.error((err.code && ISSUE_ERRORS[err.code]) || err.message),
  })
}

/** Inline block below the action row (no dialog), per mockup 751_ausgestellt. */
export function GuestLinkBlock({
  meeting,
  isAdmin,
  issued,
  onIssued,
  onRevoked,
  now,
}: {
  meeting: Meeting
  isAdmin: boolean
  /** Plaintext link from this session's issue call, if any. */
  issued: IssuedGuestLink | null
  onIssued: (link: IssuedGuestLink) => void
  onRevoked: (linkId: string) => void
  /** Clock override (tests); defaults to the current time. */
  now?: Date
}) {
  const { qc, enabled, queryKey, open, redeemed } = useGuestLinks(meeting, isAdmin, now)
  const issue = useIssueGuestLink(meeting.id, queryKey, qc, onIssued)
  const revoke = useMutation({
    mutationFn: (linkId: string) => api.revokeGuestLink(meeting.id, linkId),
    onSuccess: (_void, linkId) => {
      notify.success('Gastlink widerrufen')
      onRevoked(linkId)
      qc.invalidateQueries({ queryKey })
    },
    onError: (err: Error) => notify.error(err.message),
  })
  if (!enabled || (open.length === 0 && redeemed.length === 0)) return null
  const [current, ...others] = open
  const plain = current && issued && issued.id === current.id ? issued.url : null
  const deadline = current ? guestLinkDeadline(current, meeting) : null
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  async function onRevoke(linkId: string) {
    const ok = await confirm({
      title: 'Gastlink widerrufen?',
      description:
        'Der Link funktioniert danach nicht mehr. Wer schon im Raum ist, bleibt drin — entfernen geht in Jitsi.',
      confirmLabel: 'Widerrufen',
      variant: 'danger',
    })
    if (ok) revoke.mutate(linkId)
  }

  async function onCopy(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      notify.success('Gastlink kopiert')
    } catch {
      notify.error('Kopieren nicht möglich — Link bitte markieren und kopieren')
    }
  }

  async function onShare(url: string) {
    try {
      await navigator.share({ title: 'Einladung zum Video-Meeting', url })
    } catch {
      // abgebrochen oder nicht erlaubt — nichts zu tun
    }
  }

  return (
    <div className="rounded-lg border border-accent/30 bg-accent/5 p-3 space-y-2.5">
      <div className="flex items-center gap-1.5 text-xs font-medium text-text-primary">
        <UserPlus className="h-3.5 w-3.5 text-accent" />
        Gastlink für dieses Meeting
      </div>
      {plain ? (
        <>
          <div
            className="flex h-10 w-full items-center rounded-lg border border-border-default bg-bg-overlay/50 px-3 font-mono text-[13px] text-text-primary select-all"
            title="Gastlink"
          >
            <span className="truncate">{displayUrl(plain)}</span>
          </div>
          <div className={cn('grid gap-2', canShare ? 'grid-cols-2' : 'grid-cols-1')}>
            <button
              type="button"
              onClick={() => onCopy(plain)}
              className="inline-flex items-center justify-center gap-2 rounded-md h-10 px-3 text-xs font-medium bg-accent text-bg-base hover:bg-accent/90 transition-colors"
            >
              <Copy className="h-4 w-4" />
              Kopieren
            </button>
            {canShare && (
              <button
                type="button"
                onClick={() => onShare(plain)}
                className="inline-flex items-center justify-center gap-2 rounded-md h-10 px-3 text-xs font-medium border border-border-default bg-bg-elevated/60 text-text-primary hover:bg-bg-overlay/60 transition-colors"
              >
                <Share2 className="h-4 w-4" />
                Teilen
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2 rounded-lg border border-border-subtle bg-bg-overlay/40 px-3 py-2">
          <p className="text-[11px] text-text-secondary flex-1 min-w-0">
            {current ? 'Link wurde beim Ausstellen angezeigt.' : 'Kein offener Gastlink.'}
          </p>
          <button
            type="button"
            onClick={() => issue.mutate()}
            disabled={issue.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-accent/30 px-2 py-1 text-xs font-medium text-accent hover:bg-accent/10 transition-colors disabled:opacity-50 shrink-0"
          >
            <UserPlus className="h-3.5 w-3.5" />
            {issue.isPending ? '…' : 'Neuer Link'}
          </button>
        </div>
      )}
      {current && deadline && (
        <>
          <ul className="space-y-1 text-[11px] text-text-secondary">
            <li className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              gilt bis {stamp(deadline.at)} {deadline.byMeeting ? '(Meeting-Ende + 2 h)' : '(24 h)'}
            </li>
            <li className="flex items-center gap-1.5">
              <Ticket className="h-3.5 w-3.5 shrink-0" />
              nur einmal nutzbar, danach verfällt er
            </li>
            <li className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
              Gast sieht nur diesen Raum, sonst nichts
            </li>
          </ul>
          <div className="flex items-center justify-between pt-1 border-t border-border-subtle">
            <span className="text-2xs text-text-tertiary">
              ausgestellt {stamp(current.createdAt)}
            </span>
            <button
              type="button"
              onClick={() => onRevoke(current.id)}
              disabled={revoke.isPending}
              className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-xs text-danger hover:bg-danger/10 transition-colors disabled:opacity-50"
            >
              <Ban className="h-3.5 w-3.5" />
              Widerrufen
            </button>
          </div>
        </>
      )}
      {others.map((l) => (
        <div
          key={l.id}
          className="flex items-center justify-between gap-2 pt-1 border-t border-border-subtle"
        >
          <span className="text-2xs text-text-tertiary">
            weiterer Gastlink · ausgestellt {stamp(l.createdAt)}
          </span>
          <button
            type="button"
            onClick={() => onRevoke(l.id)}
            disabled={revoke.isPending}
            className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-xs text-danger hover:bg-danger/10 transition-colors disabled:opacity-50"
          >
            <Ban className="h-3.5 w-3.5" />
            Widerrufen
          </button>
        </div>
      ))}
      {redeemed.map((l) => (
        // Eingelöst: der gebundene Browser kann bis Meeting-Ende + 2 h
        // wiederkommen — Widerruf sperrt das sofort.
        <div
          key={l.id}
          className="flex items-center justify-between gap-2 pt-1 border-t border-border-subtle"
        >
          <span className="text-2xs text-text-tertiary">
            eingelöst {stamp(l.usedAt ?? l.createdAt)} · Gast kann wiederkommen
          </span>
          <button
            type="button"
            onClick={() => onRevoke(l.id)}
            disabled={revoke.isPending}
            className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-1 text-xs text-danger hover:bg-danger/10 transition-colors disabled:opacity-50"
          >
            <Ban className="h-3.5 w-3.5" />
            Widerrufen
          </button>
        </div>
      ))}
    </div>
  )
}

/** State shared by action + block inside one MeetingCard. */
export function useGuestLinkCardState() {
  const [issued, setIssued] = useState<IssuedGuestLink | null>(null)
  const [expanded, setExpanded] = useState(false)
  return {
    issued,
    expanded,
    toggle: () => setExpanded((v) => !v),
    onIssued: (link: IssuedGuestLink) => {
      setIssued(link)
      setExpanded(true)
    },
    onRevoked: (linkId: string) => {
      setIssued((cur) => (cur?.id === linkId ? null : cur))
    },
  }
}
